/**
 * Jun-2026 (User-Wunsch) — Marktplatzeinstellungen, gegliedert in 5 Unterbereiche.
 * Der Org-Übernahme-Schalter je Unterbereich sitzt jetzt DIREKT am Kopf des
 * jeweiligen Unterbereichs (statt in einer Sammel-Box oben).
 */
import { useState, useEffect } from "react";
import { Input } from "../ui/input";
import api from "../../lib/api";
import { toast } from "sonner";
import { Handshake, Calculator, Zap, PhoneMissed, MessageSquare, Repeat, KeyRound, Gavel, FileSignature, Lock, Eye } from "lucide-react";
import { BranchSubToggle } from "./BranchOverrideToggle";
import FavoriteBrandsPanel from "./FavoriteBrandsPanel";
import {
    OffersNotificationCard,
    TradeinNotificationCard,
    MessagesNotificationCard,
    BuyRequestAcceptanceSection,
    BuyrequestsNotificationCard,
    AuctionsNotificationCard,
} from "../DealerSettings";

function GroupHeader({ icon: Icon, iconClass = "text-[#FFCC00]", title, subKey, currentUser }) {
    return (
        <div className="flex items-center justify-between gap-3 mb-3" data-testid={`market-group-header-${subKey}`}>
            <h3 className="font-display text-base font-bold text-[#111827] flex items-center gap-2">
                <Icon className={`w-4 h-4 ${iconClass}`} /> {title}
            </h3>
            <BranchSubToggle area="marketplace" subKey={subKey} currentUser={currentUser} title={title} />
        </div>
    );
}

// Jul-2026 (User-Wunsch) — Digitale Signatur muss aktiv geschaltet werden,
// sonst sind die Signieren-Buttons im Verkaufsprozess ausgegraut.
function DigitalSignatureToggle({ currentUser }) {
    const [enabled, setEnabled] = useState(Boolean(currentUser?.digital_signature_enabled));
    const [busy, setBusy] = useState(false);
    useEffect(() => { setEnabled(Boolean(currentUser?.digital_signature_enabled)); }, [currentUser?.digital_signature_enabled]);
    const flip = async (next) => {
        setBusy(true);
        try {
            await api.patch("/users/me/preferences", { digital_signature_enabled: next });
            setEnabled(next);
            toast.success(next ? "Digitale Signatur aktiviert" : "Digitale Signatur deaktiviert");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally { setBusy(false); }
    };
    return (
        <div className="mt-6 pt-6 border-t border-gray-100">
            <label className="flex items-start gap-3 cursor-pointer" data-testid="profile-digital-signature-label">
                <input
                    type="checkbox"
                    checked={enabled}
                    disabled={busy}
                    onChange={(e) => flip(e.target.checked)}
                    className="mt-1 w-5 h-5 accent-[#FFCC00]"
                    data-testid="profile-digital-signature-checkbox"
                />
                <div className="flex-1">
                    <div className="font-bold text-[#111827] flex items-center gap-2">
                        <FileSignature className="w-4 h-4 text-[#111827]" /> Digitale Signatur aktivieren
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                        Schaltet im Verkaufsprozess die Buttons „Kaufvertrag signieren", „Ankaufvertrag signieren"
                        und „Übergabeprotokoll signieren" frei (Signatur am Gerät, per QR-Link oder Fernsignatur
                        via Skribble). Solange deaktiviert, sind die Buttons ausgegraut.
                    </div>
                </div>
            </label>
        </div>
    );
}

export default function MarketplaceSettingsSection({ currentUser, form, updateForm, savingMarket }) {
    const [org, setOrg] = useState(null);
    useEffect(() => { api.get("/org/me").then((r) => setOrg(r.data)).catch(() => {}); }, []);
    const [dealers, setDealers] = useState([]);
    useEffect(() => {
        api.get("/dealer/shared-inventory-dealers").then((r) => setDealers(r.data?.items || [])).catch(() => {});
    }, []);
    const sel = Array.isArray(form.b2b_visible_dealer_ids) ? form.b2b_visible_dealer_ids : [];
    const selCount = sel.length;
    const [dealerFilter, setDealerFilter] = useState("");
    const filteredDealers = dealers.filter((d) => {
        const q = dealerFilter.trim().toLowerCase();
        if (!q) return true;
        return `${d.name || ""} ${d.city || ""}`.toLowerCase().includes(q);
    });
    const isFiliale = !!org?.is_filiale;
    const mkt = org?.hq_overrides?.marketplace;
    // Gesperrt, wenn die Filiale diesen Teilbereich vom Hauptsitz erzwungen bekommt
    // (Übernahme an UND Teilbereich nicht ausgenommen).
    const lock = (key) => isFiliale && !!mkt?.enabled && !(mkt?.excluded || []).includes(key);
    const topMarkenLocked = isFiliale && !!org?.hq_overrides?.top_marken?.enabled;

    return (
        <div className="space-y-6" data-testid="market-groups">
            {isFiliale && (mkt?.enabled || topMarkenLocked) && (
                <div className="flex items-start gap-2 rounded-sm bg-amber-50 border border-amber-200 px-3 py-2" data-testid="market-filiale-locked-banner">
                    <Lock className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                    <p className="text-xs text-amber-900">
                        Einige Bereiche werden von Ihrem Hauptsitz vorgegeben (org-weit) und sind hier sichtbar, aber <strong>nicht änderbar</strong>.
                    </p>
                </div>
            )}

            <FavoriteBrandsPanel currentUser={currentUser} locked={topMarkenLocked} />

            {/* ============ 0) SICHTBARKEIT (B2C/B2B + Händler-Allowlist) ============ */}
            <div data-testid="market-group-visibility">
                <h3 className="font-display text-base font-bold text-[#111827] flex items-center gap-2 mb-1">
                    <Eye className="w-4 h-4 text-[#FFCC00]" /> Sichtbarkeit — wer darf meinen Fahrzeugbestand / Inserate sehen?
                </h3>
                <p className="text-xs text-gray-600 mb-3">Standard für NEUE Inserate. Mindestens eine Käufergruppe muss aktiv bleiben.</p>
                <div className="space-y-2">
                    <label className="flex items-start gap-3 cursor-pointer" data-testid="market-visibility-b2c-label">
                        <input
                            type="checkbox"
                            checked={form.default_visibility_b2c !== false}
                            disabled={savingMarket}
                            onChange={(e) => { const n = e.target.checked; if (!n && form.default_visibility_b2b === false) return; updateForm("default_visibility_b2c", n); }}
                            className="mt-1 w-5 h-5 accent-[#FFCC00]"
                            data-testid="market-visibility-b2c"
                        />
                        <span className="text-sm"><span className="font-medium">B2C — private Käufer</span><br /><span className="text-xs text-gray-500">Endkunden sehen Inserate in Suche, Detailseite und Favoriten.</span></span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer" data-testid="market-visibility-b2b-label">
                        <input
                            type="checkbox"
                            checked={form.default_visibility_b2b !== false}
                            disabled={savingMarket}
                            onChange={(e) => { const n = e.target.checked; if (!n && form.default_visibility_b2c === false) return; updateForm("default_visibility_b2b", n); }}
                            className="mt-1 w-5 h-5 accent-[#FFCC00]"
                            data-testid="market-visibility-b2b"
                        />
                        <span className="text-sm"><span className="font-medium">B2B — gewerbliche Käufer</span><br /><span className="text-xs text-gray-500">Andere Händler sehen Bestand &amp; Inserate (Aufkauf, Großhandel, Disposition). Extern für B2B freigegebene Fahrzeuge sind auch intern sichtbar.</span></span>
                    </label>
                </div>
                {form.default_visibility_b2b !== false && (
                    <div className="mt-3 border border-gray-200 rounded-sm p-3" data-testid="market-b2b-dealer-allowlist">
                        <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-bold uppercase tracking-wider text-gray-600">Welche Händler? (Mehrfachauswahl)</span>
                            <span className="text-[11px] text-gray-500" data-testid="market-b2b-sel-count">{selCount === 0 ? "Alle Händler" : `${selCount} ausgewählt`}</span>
                        </div>
                        <p className="text-[11px] text-gray-500 mb-2">Standard: alle registrierten Händler. Einzelne abwählen, um den Bestand nur den markierten Händlern zu zeigen. (Alle abgewählt = wieder für alle sichtbar.)</p>
                        <button type="button" className="text-[11px] underline text-gray-600 mb-2" onClick={() => updateForm("b2b_visible_dealer_ids", [])} data-testid="market-b2b-select-all">Alle auswählen</button>
                        <input
                            type="text"
                            value={dealerFilter}
                            onChange={(e) => setDealerFilter(e.target.value)}
                            placeholder="Händler suchen (Name oder Ort) …"
                            className="w-full mb-2 px-2.5 py-1.5 text-sm border border-gray-300 rounded-sm focus:outline-none focus:ring-1 focus:ring-[#FFCC00]"
                            data-testid="market-b2b-dealer-search"
                        />
                        <div className="max-h-56 overflow-y-auto space-y-1 pr-1">
                            {filteredDealers.map((d) => {
                                const checked = sel.length === 0 || sel.includes(d.id);
                                return (
                                    <label key={d.id} className="flex items-center gap-2 text-sm py-0.5" data-testid={`market-b2b-dealer-${d.id}`}>
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            disabled={savingMarket}
                                            onChange={() => {
                                                const allIds = dealers.map((x) => x.id);
                                                const eff = sel.length === 0 ? allIds : sel;
                                                const next = checked ? eff.filter((x) => x !== d.id) : [...eff, d.id];
                                                updateForm("b2b_visible_dealer_ids", next.length === allIds.length ? [] : next);
                                            }}
                                            className="w-4 h-4 accent-[#FFCC00]"
                                        />
                                        <span>{d.name}{d.city ? <span className="text-gray-400"> · {d.city}</span> : null}</span>
                                    </label>
                                );
                            })}
                            {filteredDealers.length === 0 && <p className="text-xs text-gray-400">{dealers.length === 0 ? "Keine weiteren registrierten Händler gefunden." : "Kein Händler passt zur Suche."}</p>}
                        </div>
                    </div>
                )}
            </div>

            {/* ============ 1) VERHANDLUNG & ANGEBOTE ============ */}
            <div data-testid="market-group-verhandlung">
                <GroupHeader icon={Handshake} title="Verhandlung & Angebote" subKey="verhandlung" currentUser={currentUser} />
                <fieldset disabled={lock("verhandlung")} className="border-0 p-0 m-0 disabled:opacity-60">
                <label className="flex items-start gap-3 cursor-pointer" data-testid="profile-allow-offers-label">
                    <input
                        type="checkbox"
                        checked={form.allow_offers}
                        disabled={savingMarket}
                        onChange={(e) => updateForm("allow_offers", e.target.checked)}
                        className="mt-1 w-5 h-5 accent-[#FFCC00]"
                        data-testid="profile-allow-offers-checkbox"
                    />
                    <div className="flex-1">
                        <div className="font-bold text-[#111827]">Preisvorschläge erlauben</div>
                        <div className="text-xs text-gray-600 mt-1">
                            Wenn aktiv, kann diese Option pro Inserat aktiviert werden. Andernfalls wird die Vorschlag-Funktion bei all Ihren Inseraten ausgeblendet — auch wenn das Inserat selbst sie zulässt.
                        </div>
                    </div>
                </label>
                {form.allow_offers && (
                    <div className="mt-6 pt-6 border-t border-gray-100">
                        <div className="font-bold text-[#111827] mb-1">Auto-Reject-Schwellwert (Prozent)</div>
                        <p className="text-xs text-gray-600 mb-3">
                            Vorschläge unter diesem Prozentsatz des Festpreises werden automatisch abgelehnt — keine E-Mail, keine Benachrichtigung an Sie.
                            Spart Zeit bei Lowball-Angeboten. Pro Inserat kann zusätzlich ein absoluter Mindestbetrag gesetzt werden.
                        </p>
                        <div className="flex gap-2 items-stretch max-w-sm">
                            <div className="relative flex-1">
                                <Input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={form.min_offer_pct}
                                    onChange={(e) => updateForm("min_offer_pct", e.target.value)}
                                    placeholder="z.B. 70"
                                    className="rounded-sm font-mono-num pr-8"
                                    data-testid="min-offer-pct-input"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold">%</span>
                            </div>
                        </div>
                        {(() => {
                            const pct = Number((form.min_offer_pct || "").toString().trim());
                            if (!Number.isFinite(pct) || pct <= 0 || pct > 100) return null;
                            const example = 10000;
                            const cutoff = Math.round((example * pct) / 100);
                            return (
                                <div
                                    className="mt-3 flex items-start gap-1.5 rounded-sm bg-[#FFFBEB] border border-[#FDE68A] px-3 py-2 max-w-sm"
                                    data-testid="min-offer-pct-example"
                                >
                                    <Calculator className="w-3.5 h-3.5 text-[#B45309] mt-0.5 shrink-0" />
                                    <span className="text-[11px] text-[#92400E] leading-snug">
                                        Beispiel: bei <strong>{example.toLocaleString("de-DE")} €</strong> Festpreis werden
                                        Vorschläge unter <strong>{cutoff.toLocaleString("de-DE")} €</strong> automatisch abgelehnt
                                        (= {pct} %).
                                    </span>
                                </div>
                            );
                        })()}
                        {currentUser.min_offer_pct !== undefined && currentUser.min_offer_pct !== null && (
                            <p className="text-[11px] text-gray-500 mt-2">
                                Aktuell aktiv: <strong>{currentUser.min_offer_pct} %</strong>
                            </p>
                        )}
                        <label
                            className="mt-4 flex items-start gap-3 cursor-pointer rounded-sm border border-gray-200 p-3 hover:bg-[#FFFBEB]/40"
                            data-testid="auto-counter-offers-label"
                        >
                            <input
                                type="checkbox"
                                checked={form.auto_counter_offers}
                                disabled={savingMarket}
                                onChange={(e) => updateForm("auto_counter_offers", e.target.checked)}
                                className="mt-1 w-5 h-5 accent-[#FFCC00]"
                                data-testid="auto-counter-offers-checkbox"
                            />
                            <div className="flex-1">
                                <div className="font-bold text-[#111827] flex items-center gap-1.5">
                                    <Handshake className="w-4 h-4 text-[#FFCC00]" />
                                    Automatischer Gegenvorschlag statt Ablehnung
                                </div>
                                <div className="text-xs text-gray-600 mt-0.5">
                                    Statt Lowball-Vorschläge unter dem Schwellwert nur abzulehnen, sendet TwoWheels automatisch
                                    einen Gegenvorschlag in Höhe des Schwellwerts — der Interessent bleibt im Gespräch und kann
                                    direkt annehmen. Greift nur, wenn ein Auto-Reject-Schwellwert (Prozent oben oder absoluter
                                    €-Wert pro Inserat) gesetzt ist.
                                </div>
                            </div>
                        </label>
                        {currentUser.role === "b2b" && (
                            <OffersNotificationCard currentUser={currentUser} values={form} onChange={(k, v) => updateForm(k, v)} />
                        )}
                    </div>
                )}
                </fieldset>
            </div>

            {/* ============ 2) KOMMUNIKATION (Chat / WhatsApp / Anruf) ============ */}
            <div className="pt-6 border-t border-gray-100" data-testid="market-group-kommunikation">
                <GroupHeader icon={MessageSquare} iconClass="text-[#111827]" title="Kommunikation (Chat / WhatsApp / Anruf)" subKey="kommunikation" currentUser={currentUser} />
                <fieldset disabled={lock("kommunikation")} className="border-0 p-0 m-0 disabled:opacity-60">

                <label className="flex items-start gap-3 cursor-pointer" data-testid="profile-allow-chat-label">
                    <input
                        type="checkbox"
                        checked={form.allow_chat}
                        disabled={savingMarket}
                        onChange={(e) => updateForm("allow_chat", e.target.checked)}
                        className="mt-1 w-5 h-5 accent-[#FFCC00]"
                        data-testid="profile-allow-chat-checkbox"
                    />
                    <div className="flex-1">
                        <div className="font-bold text-[#111827] flex items-center gap-2">
                            <MessageSquare className="w-4 h-4 text-[#111827]" /> Direkt-Chat erlauben
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                            Andere User können Sie über die Inserate kontaktieren. Wenn deaktiviert, werden eingehende Nachrichten abgelehnt.
                            Zur Streitschlichtung kann der Plattform-Betreiber Chats einsehen.
                        </div>
                    </div>
                </label>
                {form.allow_chat && currentUser.role === "b2b" && (
                    <MessagesNotificationCard currentUser={currentUser} values={form} onChange={(k, v) => updateForm(k, v)} />
                )}

                <div className="mt-6 pt-6 border-t border-gray-100" data-testid="missed-call-settings">
                    <label className="flex items-start gap-3 cursor-pointer" data-testid="missed-call-label">
                        <input
                            type="checkbox"
                            checked={form.missed_call_alerts_enabled}
                            disabled={savingMarket}
                            onChange={(e) => updateForm("missed_call_alerts_enabled", e.target.checked)}
                            className="mt-1 w-5 h-5 accent-[#FFCC00]"
                            data-testid="missed-call-checkbox"
                        />
                        <div className="flex-1">
                            <div className="font-bold text-[#111827] flex items-center gap-2">
                                <PhoneMissed className="w-4 h-4 text-[#111827]" /> Verpasste Anrufe melden
                            </div>
                            <div className="text-xs text-gray-600 mt-1">
                                Sie werden benachrichtigt, wenn ein Interessent angerufen hat und das Gespräch nicht angenommen wurde — oder über den „Rückruf anfordern"-Button im Inserat einen Rückruf erbittet. Benachrichtigung im Dashboard, optional per E-Mail und SMS.
                            </div>
                        </div>
                    </label>
                    <div className="mt-4 border-t border-gray-100 pt-4" data-testid="whatsapp-activate-block">
                        <label className="flex items-start gap-3 cursor-pointer">
                            <input type="checkbox" checked={form.whatsapp_enabled} disabled={savingMarket} onChange={(e) => updateForm("whatsapp_enabled", e.target.checked)} className="mt-1 w-5 h-5 accent-[#25D366]" data-testid="whatsapp-enabled-checkbox" />
                            <div className="flex-1">
                                <div className="font-bold text-[#111827] flex items-center gap-2">
                                    <MessageSquare className="w-4 h-4 text-[#128C7E]" /> WhatsApp aktivieren
                                </div>
                                <div className="text-xs text-gray-600 mt-1">
                                    Bieten Sie WhatsApp als Alternative zu SMS an. Überall wo SMS gewählt werden kann, erscheint dann zusätzlich eine WhatsApp-Option. <span className="text-amber-700">Für den WhatsApp-Versand entstehen Kosten gemäß aktueller Preisliste.</span>
                                </div>
                            </div>
                        </label>
                        {form.whatsapp_enabled && (
                            <div className="mt-2 ml-8">
                                <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">WhatsApp-Nummer</label>
                                <Input value={form.whatsapp_number} disabled={savingMarket} onChange={(e) => updateForm("whatsapp_number", e.target.value)} placeholder="+49 170 …" className="mt-1 rounded-sm font-mono-num max-w-xs" data-testid="whatsapp-number-input" />
                            </div>
                        )}
                    </div>
                    {form.missed_call_alerts_enabled && (
                        <div className="mt-3 ml-8 space-y-3" data-testid="missed-call-channels">
                            <label className="flex items-center gap-2 cursor-pointer text-sm">
                                <input type="checkbox" checked={form.missed_call_email_enabled} disabled={savingMarket} onChange={(e) => updateForm("missed_call_email_enabled", e.target.checked)} className="w-4 h-4 accent-[#FFCC00]" data-testid="missed-call-email-checkbox" />
                                Benachrichtigung per E-Mail
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer text-sm">
                                <input type="checkbox" checked={form.missed_call_sms_enabled} disabled={savingMarket} onChange={(e) => updateForm("missed_call_sms_enabled", e.target.checked)} className="w-4 h-4 accent-[#FFCC00]" data-testid="missed-call-sms-checkbox" />
                                Benachrichtigung per SMS (an Ihre hinterlegte Handynummer)
                            </label>
                            <div className="text-[11px] text-amber-700 -mt-1.5 ml-6" data-testid="missed-call-sms-cost-hint">
                                Hinweis: Für den SMS-Versand entstehen Kosten gemäß aktueller Preisliste.
                            </div>
                            {form.whatsapp_enabled && (
                                <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="missed-call-whatsapp-row">
                                    <input type="checkbox" checked={form.missed_call_whatsapp_enabled} disabled={savingMarket} onChange={(e) => updateForm("missed_call_whatsapp_enabled", e.target.checked)} className="w-4 h-4 accent-[#25D366]" data-testid="missed-call-whatsapp-checkbox" />
                                    Auch per <span className="font-bold text-[#128C7E]">WhatsApp</span> senden
                                    <span className="text-[11px] text-amber-700">(Kosten gemäß aktueller Preisliste)</span>
                                </label>
                            )}
                            <button
                                type="button"
                                onClick={async () => {
                                    try {
                                        await api.post("/dealer/missed-calls/test");
                                        toast.success("Test-Benachrichtigung erstellt — siehe Dashboard-Tab Verpasste Anrufe.");
                                    } catch {
                                        toast.error("Test fehlgeschlagen");
                                    }
                                }}
                                className="text-xs font-bold uppercase tracking-wider text-[#2563EB] hover:underline"
                                data-testid="missed-call-test-btn"
                            >
                                Test-Benachrichtigung senden
                            </button>
                        </div>
                    )}
                </div>
                </fieldset>
            </div>

            {/* ============ 3) SOFORTKAUF & VERTRAG ============ */}
            <div className="pt-6 border-t border-gray-100" data-testid="market-group-sofortkauf">
                <GroupHeader icon={Zap} title="Sofortkauf & Vertrag" subKey="sofortkauf" currentUser={currentUser} />
                <fieldset disabled={lock("sofortkauf")} className="border-0 p-0 m-0 disabled:opacity-60">

                <div data-testid="instant-buy-section">
                    <label className="flex items-start gap-3 cursor-pointer" data-testid="instant-buy-label">
                        <input
                            type="checkbox"
                            checked={form.instant_buy_enabled}
                            disabled={savingMarket}
                            onChange={(e) => updateForm("instant_buy_enabled", e.target.checked)}
                            className="mt-1 w-5 h-5 accent-[#FFCC00]"
                            data-testid="instant-buy-checkbox"
                        />
                        <div className="flex-1">
                            <div className="font-bold text-[#111827] flex items-center gap-1.5">
                                <Zap className="w-4 h-4 text-[#FFCC00]" /> Sofortkauf erlauben
                            </div>
                            <div className="text-xs text-gray-600 mt-0.5">
                                Zeigt auf Festpreis-Inseraten einen „Jetzt verbindlich kaufen"-Button. Käufer schließen ohne
                                Verhandlung direkt ab (Reservierung → Kaufvertrag-PDF → E-Mail). Pro Inserat überschreibbar.
                            </div>
                        </div>
                    </label>
                    {form.instant_buy_enabled && (
                        <div className="mt-3 ml-8 space-y-3" data-testid="instant-buy-payment-settings">
                            <label className="flex items-start gap-3 cursor-pointer" data-testid="instant-buy-payment-label">
                                <input
                                    type="checkbox"
                                    checked={form.instant_buy_payment_enabled}
                                    disabled={savingMarket}
                                    onChange={(e) => updateForm("instant_buy_payment_enabled", e.target.checked)}
                                    className="mt-1 w-5 h-5 accent-[#FFCC00]"
                                    data-testid="instant-buy-payment-checkbox"
                                />
                                <div className="flex-1">
                                    <div className="font-bold text-[#111827] text-sm">Online-Zahlung beim Sofortkauf (Stripe)</div>
                                    <div className="text-xs text-gray-600 mt-0.5">
                                        Käufer bezahlen beim verbindlichen Kauf direkt online. Ohne Aktivierung läuft der Sofortkauf
                                        weiterhin nur über Reservierung + Kaufvertrag (ohne Zahlung).
                                    </div>
                                </div>
                            </label>
                            {form.instant_buy_payment_enabled && (
                                <div className="ml-8" data-testid="instant-buy-deposit-row">
                                    <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
                                        Anzahlung (€) — optional
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        step="1"
                                        value={form.instant_buy_deposit_eur}
                                        disabled={savingMarket}
                                        onChange={(e) => updateForm("instant_buy_deposit_eur", e.target.value)}
                                        placeholder="z.B. 500"
                                        className="mt-1 block w-40 rounded-sm border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#FFCC00] outline-none"
                                        data-testid="instant-buy-deposit-input"
                                    />
                                    <div className="text-xs text-gray-500 mt-1">
                                        Wenn gesetzt, kann der Käufer zwischen Anzahlung und Vollzahlung wählen. Leer = nur Vollzahlung.
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {currentUser.role === "b2b" && (
                    <div className="mt-6 pt-6 border-t border-gray-100" data-testid="auto-contract-section">
                        <label className="flex items-start gap-3 cursor-pointer" data-testid="auto-contract-toggle-label">
                            <input
                                type="checkbox"
                                checked={form.auto_contract_dispatch_enabled}
                                disabled={savingMarket}
                                onChange={(e) => updateForm("auto_contract_dispatch_enabled", e.target.checked)}
                                className="mt-1 w-5 h-5 accent-[#FFCC00] shrink-0"
                                data-testid="auto-contract-enabled-checkbox"
                            />
                            <div className="flex-1">
                                <div className="font-bold text-[#111827] flex items-center gap-2">
                                    <FileSignature className="w-4 h-4 text-[#FFCC00]" /> Automatischer Vertragsversand
                                </div>
                                <div className="text-xs text-gray-600 mt-1">
                                    Bei Reservierung oder Kauf wird automatisch ein Kaufvertrag auf Basis Ihrer Profildaten erstellt
                                    und mit Reservierungs-Countdown verschickt. Käufer sieht den verbleibenden Countdown bis Ablauf der Reservierung.
                                    Speichert Reservierungsdauer und Finanzierungs-Link — der eigentliche Versand wird in einer Folge-Phase
                                    live geschaltet (sobald die E-Mail-Anbindung aktiv ist).
                                </div>
                            </div>
                        </label>
                        {form.auto_contract_dispatch_enabled && (
                            <div className="ml-7 mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="auto-contract-fields">
                                <div>
                                    <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Reservierungsdauer in Stunden</label>
                                    <Input
                                        type="number"
                                        min="1"
                                        max="720"
                                        value={form.reservation_hours}
                                        onChange={(e) => updateForm("reservation_hours", e.target.value)}
                                        className="rounded-sm mt-1 h-9 font-mono-num"
                                        data-testid="reservation-hours-input"
                                    />
                                    <div className="text-[10px] text-gray-500 mt-1">Empfohlen: 48 Stunden. Maximal 720 (= 30 Tage).</div>
                                </div>
                                <div>
                                    <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Finanzierungs-Link (optional)</label>
                                    <Input
                                        type="url"
                                        value={form.financing_link_url}
                                        onChange={(e) => updateForm("financing_link_url", e.target.value)}
                                        placeholder="https://finanzierung.ihre-bank.de"
                                        className="rounded-sm mt-1 h-9 font-mono-num text-sm"
                                        data-testid="financing-link-input"
                                    />
                                    <div className="text-[10px] text-gray-500 mt-1">Wird im Reservierungs-Mail als Button eingebunden.</div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                </fieldset>
            </div>

            {/* ============ 4) AUKTIONEN ============ */}
            <div className="pt-6 border-t border-gray-100" data-testid="market-group-auktionen">
                <GroupHeader icon={Gavel} iconClass="text-[#DC2626]" title="Auktionen" subKey="auktionen" currentUser={currentUser} />
                <fieldset disabled={lock("auktionen")} className="border-0 p-0 m-0 disabled:opacity-60">
                <label className="flex items-start gap-3 cursor-pointer" data-testid="profile-allow-auctions-label">
                    <input
                        type="checkbox"
                        checked={form.allow_auctions}
                        disabled={savingMarket}
                        onChange={(e) => updateForm("allow_auctions", e.target.checked)}
                        className="mt-1 w-5 h-5 accent-[#DC2626]"
                        data-testid="profile-allow-auctions-checkbox"
                    />
                    <div className="flex-1">
                        <div className="font-bold text-[#111827] flex items-center gap-2">
                            <Gavel className="w-4 h-4 text-[#DC2626]" /> Auktionen anbieten / mitbieten
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                            Wenn aktiv, können Sie eigene Inserate als Auktion einstellen <strong>und</strong> auf fremde Auktionen bieten. Auktionen nutzen das Gebotssystem (Startpreis, Sofort-Kaufen, Laufzeit) — die anderen Marktplatzeinstellungen oben gelten nur für Festpreis-Inserate.
                        </div>
                    </div>
                </label>
                {form.allow_auctions && (
                    <div className="mt-3 ml-7 border-l-2 border-[#DC2626]/30 pl-4" data-testid="escrow-sub-block">
                        <label className="flex items-start gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={!!form.use_escrow}
                                disabled={savingMarket}
                                onChange={(e) => updateForm("use_escrow", e.target.checked)}
                                className="mt-1 w-4 h-4 accent-[#DC2626]"
                                data-testid="profile-use-escrow-checkbox"
                            />
                            <div className="flex-1">
                                <div className="font-bold text-sm text-[#111827]">Treuhand / Kautionkonto-Funktion nutzen</div>
                                <div className="text-[11px] text-gray-600 mt-0.5">
                                    Sicherer Zahlungsfluss über ein Treuhandkonto — Käufer hinterlegt den Betrag,
                                    Auszahlung erfolgt nach erfolgter Übergabe. Empfohlen für höherwertige Auktionen.
                                </div>
                            </div>
                        </label>
                    </div>
                )}
                {form.allow_auctions && currentUser.role === "b2b" && (
                    <AuctionsNotificationCard currentUser={currentUser} values={form} onChange={(k, v) => updateForm(k, v)} />
                )}
                </fieldset>
            </div>

            {/* ============ 5) INZAHLUNGNAHME · PROBEFAHRT · ANKAUF · PROGRAMME ============ */}
            <div className="pt-6 border-t border-gray-100" data-testid="market-group-weiteres">
                <GroupHeader icon={Repeat} iconClass="text-[#111827]" title="Inzahlungnahme · Probefahrt · Ankauf · Programme" subKey="weiteres" currentUser={currentUser} />
                <fieldset disabled={lock("weiteres")} className="border-0 p-0 m-0 disabled:opacity-60">

                <label className="flex items-start gap-3 cursor-pointer" data-testid="profile-allow-tradein-label">
                    <input
                        type="checkbox"
                        checked={form.allow_tradein}
                        disabled={savingMarket}
                        onChange={(e) => updateForm("allow_tradein", e.target.checked)}
                        className="mt-1 w-5 h-5 accent-[#FFCC00]"
                        data-testid="profile-allow-tradein-checkbox"
                    />
                    <div className="flex-1">
                        <div className="font-bold text-[#111827] flex items-center gap-2">
                            <Repeat className="w-4 h-4 text-[#111827]" /> Inzahlungnahmen erlauben
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                            Wenn aktiv, kann pro Inserat „Inzahlungnahme akzeptieren" angehakt werden. Interessenten können ihr Gebrauchtfahrzeug in Zahlung anbieten und die Differenz als Zuzahlung leisten.
                        </div>
                    </div>
                </label>
                {form.allow_tradein && currentUser.role === "b2b" && (
                    <TradeinNotificationCard currentUser={currentUser} values={form} onChange={(k, v) => updateForm(k, v)} />
                )}

                <div className="mt-6 pt-6 border-t border-gray-100">
                    <label className="flex items-start gap-3 cursor-pointer" data-testid="profile-allow-test-ride-label">
                        <input
                            type="checkbox"
                            checked={form.allow_test_ride}
                            disabled={savingMarket}
                            onChange={(e) => updateForm("allow_test_ride", e.target.checked)}
                            className="mt-1 w-5 h-5 accent-[#FFCC00]"
                            data-testid="profile-allow-test-ride-checkbox"
                        />
                        <div className="flex-1">
                            <div className="font-bold text-[#111827] flex items-center gap-2">
                                <KeyRound className="w-4 h-4 text-[#111827]" /> Probefahrten erlauben
                            </div>
                            <div className="text-xs text-gray-600 mt-1">
                                Wenn aktiv, kann pro Inserat „Probefahrten anbieten" angehakt werden. Interessenten können direkt aus dem Inserat eine Probefahrt-Anfrage stellen — erhöht die Conversion vor Ort.
                            </div>
                        </div>
                    </label>
                </div>

                <DigitalSignatureToggle currentUser={currentUser} />

                {currentUser.role === "b2b" && (
                    <div className="mt-6 pt-6 border-t border-gray-100">
                        <BuyRequestAcceptanceSection
                            currentUser={currentUser}
                            embedded
                            controlled={{
                                enabled: form.accept_buy_requests,
                                termsAccepted: form.terms_accepted,
                                onEnabledChange: (v) => updateForm("accept_buy_requests", v),
                                onTermsChange: (v) => updateForm("terms_accepted", v),
                            }}
                        />
                        {form.accept_buy_requests && (
                            <BuyrequestsNotificationCard currentUser={currentUser} values={form} onChange={(k, v) => updateForm(k, v)} />
                        )}
                    </div>
                )}

                <div className="mt-6 pt-6 border-t border-gray-100">
                    <label className="flex items-start gap-3 cursor-pointer" data-testid="profile-sharing-label">
                        <input
                            type="checkbox"
                            checked={!!form.sharing_enabled}
                            disabled={savingMarket}
                            onChange={(e) => updateForm("sharing_enabled", e.target.checked)}
                            className="mt-1 w-5 h-5 accent-[#16A34A]"
                            data-testid="profile-sharing-checkbox"
                        />
                        <div className="flex-1">
                            <div className="font-bold text-[#111827] flex items-center gap-2">
                                <Repeat className="w-4 h-4 text-[#16A34A]" /> Sharing / Vermietung
                            </div>
                            <div className="text-xs text-gray-600 mt-1">
                                Schwer verkäufliche Fahrzeuge zur Kurzzeit-Nutzung anbieten. Verfügbare Zeiträume
                                erscheinen direkt am Inserat — Käufer können das Fahrzeug für die gewählten Perioden mieten.
                            </div>
                        </div>
                    </label>
                    {form.sharing_enabled && (
                        <div className="mt-3 ml-7 border-l-2 border-[#16A34A]/30 pl-4 grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="sharing-periods-block">
                            {[
                                { key: "sharing_period_week", label: "Woche", hint: "Mietzeitraum 7 Tage" },
                                { key: "sharing_period_month", label: "Monat", hint: "Mietzeitraum 30 Tage" },
                                { key: "sharing_period_season", label: "Saison von Anfang März – Ende Oktober", hint: "Klassische Sommer-Saison" },
                            ].map((p) => (
                                <label key={p.key} className="flex items-start gap-2 cursor-pointer p-2 rounded-sm bg-[#16A34A]/5 hover:bg-[#16A34A]/10">
                                    <input
                                        type="checkbox"
                                        checked={!!form[p.key]}
                                        onChange={(e) => updateForm(p.key, e.target.checked)}
                                        className="mt-1 w-4 h-4 accent-[#16A34A]"
                                        data-testid={`sharing-period-${p.key}`}
                                    />
                                    <div className="flex-1">
                                        <div className="font-bold text-sm text-[#111827]">{p.label}</div>
                                        <div className="text-[10px] text-gray-500">{p.hint}</div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    )}
                </div>
                </fieldset>
            </div>

            <p className="text-xs text-gray-500" data-testid="market-autosave-hint">Änderungen werden automatisch gespeichert.</p>
        </div>
    );
}
